class ProductLightbox extends HTMLElement {
  // Movement budget below which a gallery touch still counts as a tap.
  static TAP_SLOP = 10;
  // How long a programmatic smooth scroll is allowed to hold the scroll-sync
  // lock before it is released anyway — a swipe can cancel one mid-flight, and
  // the landing position would then never arrive to clear it.
  static SETTLE_TIMEOUT = 700;
  // Touch zoom: the ceiling a pinch may reach, and where a double tap lands.
  // Images are served up to 2400px wide, so 4x still has pixels behind it on a
  // phone-sized viewport.
  static MAX_SCALE = 4;
  static TAP_ZOOM_SCALE = 2.5;
  // Two taps count as a double tap within this window and this distance apart.
  static DOUBLE_TAP_MS = 300;
  static DOUBLE_TAP_SLOP = 30;

  connectedCallback() {
    this._dialog = this.querySelector('[data-lightbox-dialog]');
    this._slides = [...this.querySelectorAll('[data-lightbox-slide]')];
    if (!this._dialog || !this._slides.length) return;

    this._stage = this.querySelector('[data-lightbox-stage]');
    this._counter = this.querySelector('[data-lightbox-counter]');
    this._index = 0;
    this._targetIndex = null;
    this._settleTimer = 0;
    this._zoomed = false;
    // Touch zoom transform: scale, and translation in un-scaled pixels.
    this._scale = 1;
    this._tx = 0;
    this._ty = 0;
    this._canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this._bindChrome();
    this._bindStage();
    this._bindZoom();
    this._bindTouchZoom();
    this._bindGalleryTrigger();
  }

  // ── Public API ────────────────────────────────────────────────────────

  open(mediaId) {
    const target = this._slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    // Scroll lock is handled by critical.css via the dialog's scroll-lock attribute.
    if (!this._dialog.open) {
      this._dialog.showModal();
      // showModal() otherwise focuses the close button, painting a focus ring on
      // open even for mouse users. Focusing the dialog (tabindex="-1") keeps the
      // dialog announced to screen readers without the visible ring.
      this._dialog.focus();
    }
    // After showModal(), never before: the stage has no width while the dialog
    // is closed, so the scroll offset would resolve to 0 and always land on the
    // first image.
    this._goTo(target === -1 ? 0 : target, 'instant');
  }

  close() {
    if (this._dialog.open) this._dialog.close();
  }

  // ── Internals ─────────────────────────────────────────────────────────

  _activeImg() {
    const slide = this._slides[this._index];
    return slide ? slide.querySelector('img') : null;
  }

  // Records the slide the stage has landed on. Scrolling is what moves the
  // track — this only reflects the result, whether the scroll came from a
  // finger or from _goTo().
  _setIndex(index) {
    this._resetZoom();
    this._index = index;

    if (this._counter) this._counter.textContent = `${index + 1} / ${this._slides.length}`;

    this.dispatchEvent(
      new CustomEvent('lightbox:change', {
        detail: { mediaId: this._slides[index].dataset.mediaId },
      })
    );
  }

  // Scrolls the track to a slide. Wrapping past either end would otherwise
  // animate across every slide in between, so those jumps are instant.
  _goTo(nextIndex, behavior = 'smooth') {
    const total = this._slides.length;
    const index = ((nextIndex % total) + total) % total;
    const width = this._stage.clientWidth;

    const jump =
      behavior === 'instant' || this._reduceMotion.matches || Math.abs(index - this._index) > 1;

    window.clearTimeout(this._settleTimer);
    if (jump) {
      // An instant scroll lands synchronously, so there are no intermediate
      // positions to sit out.
      this._targetIndex = null;
    } else {
      this._targetIndex = index;
      this._settleTimer = window.setTimeout(() => {
        this._targetIndex = null;
      }, ProductLightbox.SETTLE_TIMEOUT);
    }

    this._stage.scrollTo({ left: width * index, behavior: jump ? 'instant' : 'smooth' });
    this._setIndex(index);
  }

  // The scroll position is the source of truth for which image is showing, so
  // a swipe and an arrow press both land here.
  _bindStage() {
    if (!this._stage) return;

    let frame = 0;
    this._stage.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const width = this._stage.clientWidth;
          if (!width) return;
          const index = Math.round(this._stage.scrollLeft / width);
          if (!this._slides[index]) return;

          // A smooth scroll passes over intermediate positions on its way; the
          // index it is heading for is already applied, so ignore them until it
          // arrives.
          if (this._targetIndex !== null) {
            if (index === this._targetIndex) this._targetIndex = null;
            return;
          }

          if (index !== this._index) this._setIndex(index);
        });
      },
      { passive: true }
    );

    // A resize changes the slide width, so the offset that holds the current
    // slide moves with it. It also invalidates the cached zoom box, which
    // cannot be re-measured while the transform is applied.
    window.addEventListener('resize', () => {
      if (!this._dialog.open) return;
      this._resetZoom();
      this._stage.scrollTo({ left: this._stage.clientWidth * this._index, behavior: 'instant' });
    });
  }

  _bindChrome() {
    const closeBtn = this.querySelector('[data-lightbox-close]');
    const prevBtn = this.querySelector('[data-lightbox-prev]');
    const nextBtn = this.querySelector('[data-lightbox-next]');

    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (prevBtn) prevBtn.addEventListener('click', () => this._goTo(this._index - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => this._goTo(this._index + 1));

    // Covers the X button, Esc, and close() alike.
    this._dialog.addEventListener('close', () => this._resetZoom());

    // The dialog fills the viewport, so this only fires on the padding around
    // the stage — never on the image itself.
    this._dialog.addEventListener('click', (e) => {
      if (e.target === this._dialog) this.close();
    });

    this._dialog.addEventListener('keydown', (e) => {
      if (this._slides.length < 2) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this._goTo(this._index - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._goTo(this._index + 1);
      }
    });
  }

  // ── Zoom ──────────────────────────────────────────────────────────────
  //
  // Two implementations, picked by input type. Pointers get transform-origin
  // magnification that tracks the cursor; touch gets a pinch/pan transform it
  // drives itself, because the browser's own pinch zooms the whole page and
  // would carry the close button and arrows off-screen with it.

  _resetZoom() {
    const img = this._activeImg();
    this._zoomed = false;
    this._baseRect = null;
    this._scale = 1;
    this._tx = 0;
    this._ty = 0;
    if (this._stage) this._stage.classList.remove('is-zoomed');
    if (!img) return;
    img.classList.remove('is-zoomed');
    img.style.transformOrigin = '';
    // Clearing the inline transition hands the property back to the CSS rule,
    // so dropping the transform animates the image back to its resting size.
    img.style.transition = '';
    img.style.transform = '';
  }

  // Anchor the transform at a point inside the image box, as a percentage, so
  // scaling magnifies around exactly that point.
  //
  // Measured against _baseRect — the box captured *before* zooming — because
  // getBoundingClientRect() reports the scaled visual box once the transform is
  // applied. Using that would map the cursor to a fraction of a box twice the
  // intended size, most of which is clipped off-screen, so panning would drift
  // and never reach the edges of the image.
  _setOrigin(img, clientX, clientY) {
    const rect = this._baseRect;
    if (!rect || !rect.width || !rect.height) return;
    const x = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1) * 100;
    const y = Math.min(Math.max((clientY - rect.top) / rect.height, 0), 1) * 100;
    img.style.transformOrigin = `${x}% ${y}%`;
  }

  // The responsive pick is sized for the fit view, so it would upscale once
  // magnified — swap in the widest srcset candidate first.
  _upgradeSrc(img) {
    const srcset = img.getAttribute('srcset');
    if (!srcset) return;

    let best = { url: img.currentSrc || img.src, w: 0 };
    srcset.split(',').forEach((entry) => {
      const [url, descriptor] = entry.trim().split(/\s+/);
      const w = parseInt(descriptor, 10) || 0;
      if (w > best.w) best = { url, w };
    });

    if (best.url && img.src !== best.url) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = best.url;
    }
  }

  // ── Pointer zoom ──────────────────────────────────────────────────────

  // Click-to-zoom is mouse-only; touch is served by _bindTouchZoom instead,
  // where a tap has to be told apart from a swipe.
  _bindZoom() {
    if (!this._stage || !this._canHover) return;

    this._stage.addEventListener('click', (e) => {
      const img = e.target.closest('.product-lightbox__img');
      if (!img) return;

      if (this._zoomed) {
        this._resetZoom();
        return;
      }

      // Capture the un-transformed box first — everything else depends on it.
      this._baseRect = img.getBoundingClientRect();
      this._upgradeSrc(img);
      this._setOrigin(img, e.clientX, e.clientY);
      img.classList.add('is-zoomed');
      this._zoomed = true;
    });

    // A resize invalidates the cached box, and there is no way to re-measure it
    // while the transform is applied — _bindStage clears the zoom on resize.

    this._stage.addEventListener('mousemove', (e) => {
      if (!this._zoomed) return;
      const img = this._activeImg();
      if (!img) return;
      // Panning tracks the cursor 1:1, so no easing on origin updates.
      img.style.transition = 'none';
      this._setOrigin(img, e.clientX, e.clientY);
    });
  }

  // ── Touch zoom ────────────────────────────────────────────────────────

  _rectCenter() {
    const rect = this._baseRect;
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  // Stops the image being dragged off into empty space: it may travel only as
  // far as its own edges, and not at all until it outgrows the stage.
  //
  // The limits are measured around the image's resting position rather than the
  // centre of the stage. The two differ by half the difference between the top
  // and bottom padding — a few pixels — and anchoring to the resting position
  // means the image does not shift when a pinch settles back to 1.
  _clampPan() {
    const rect = this._baseRect;
    if (!rect) return;
    const rangeX = Math.max(0, (rect.width * this._scale - this._stage.clientWidth) / 2);
    const rangeY = Math.max(0, (rect.height * this._scale - this._stage.clientHeight) / 2);
    this._tx = Math.min(Math.max(this._tx, -rangeX), rangeX);
    this._ty = Math.min(Math.max(this._ty, -rangeY), rangeY);
  }

  // translate before scale, against the CSS `transform-origin: center center`,
  // so _clampPan's ranges stay in un-scaled pixels.
  _applyZoom(img, animate) {
    this._clampPan();
    this._zoomed = this._scale > 1;
    if (this._zoomed) this._upgradeSrc(img);

    // While the fingers are down the transform has to follow them exactly, so
    // easing is suppressed until the gesture ends.
    img.style.transition = animate ? '' : 'none';
    img.style.transform = `translate(${this._tx}px, ${this._ty}px) scale(${this._scale})`;

    // Handing the stage to the zoom stops the carousel stealing a pan as a
    // swipe; the two gestures are otherwise identical.
    this._stage.classList.toggle('is-zoomed', this._zoomed);
  }

  // Zoom about a point: the image content under (x, y) stays under (x, y).
  // With t = 0 and s = 1 to begin with, the general anchor solve below reduces
  // to offset × (1 - scale).
  _tapZoom(img, x, y) {
    if (this._zoomed) {
      this._resetZoom();
      return;
    }

    // Capture the un-transformed box first — everything else depends on it.
    this._baseRect = img.getBoundingClientRect();
    const center = this._rectCenter();
    this._scale = ProductLightbox.TAP_ZOOM_SCALE;
    this._tx = (x - center.x) * (1 - this._scale);
    this._ty = (y - center.y) * (1 - this._scale);
    this._applyZoom(img, true);
  }

  // Pinch and double-tap zoom, run in-element so the close button and arrows
  // stay pinned to the viewport — a page-level pinch would scroll them out of
  // reach. The stage carries `touch-action: pan-x`, which leaves the carousel
  // swipe to the browser but routes every pinch here; once zoomed it switches
  // to `touch-action: none` so a drag pans the image instead of changing slide.
  _bindTouchZoom() {
    if (!this._stage || this._canHover) return;

    const spread = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    // Live gesture state. `pinch` and `pan` hold the transform as it was when
    // the fingers went down; every move re-solves from there rather than
    // accumulating deltas, so rounding cannot drift over a long gesture.
    let pinch = null;
    let pan = null;
    let startX = 0;
    let startY = 0;
    let moved = false;
    let lastTap = 0;
    let lastTapX = 0;
    let lastTapY = 0;

    // A pan is measured from startX/startY, which every touchstart records.
    const startPan = (touch) => {
      startX = touch.clientX;
      startY = touch.clientY;
      moved = false;
      return { tx: this._tx, ty: this._ty };
    };

    this._stage.addEventListener(
      'touchstart',
      (e) => {
        const img = this._activeImg();
        if (!img) return;

        if (e.touches.length === 2) {
          e.preventDefault();
          pan = null;
          // At rest the transform is identity, so this reads the true box. Once
          // zoomed the cached one still stands — getBoundingClientRect would
          // report the scaled box and corrupt every solve that follows.
          if (!this._baseRect) this._baseRect = img.getBoundingClientRect();

          const [a, b] = [e.touches[0], e.touches[1]];
          pinch = {
            spread: spread(a, b) || 1,
            scale: this._scale,
            tx: this._tx,
            ty: this._ty,
            x: (a.clientX + b.clientX) / 2,
            y: (a.clientY + b.clientY) / 2,
          };
          return;
        }

        if (e.touches.length === 1) {
          if (this._zoomed) e.preventDefault();
          const started = startPan(e.touches[0]);
          pan = this._zoomed ? started : null;
        }
      },
      { passive: false }
    );

    this._stage.addEventListener(
      'touchmove',
      (e) => {
        const img = this._activeImg();
        if (!img) return;

        if (pinch && e.touches.length >= 2) {
          e.preventDefault();
          const [a, b] = [e.touches[0], e.touches[1]];
          const scale = Math.min(
            Math.max((spread(a, b) / pinch.spread) * pinch.scale, 1),
            ProductLightbox.MAX_SCALE
          );
          const ratio = scale / pinch.scale;
          const center = this._rectCenter();
          const x = (a.clientX + b.clientX) / 2;
          const y = (a.clientY + b.clientY) / 2;

          // Solve for the translation that keeps the image content under the
          // pinch midpoint there, as the fingers both spread and travel:
          //   screen(p) = centre + t + scale × (p - centre)
          // pinned at the midpoint gives t' = v' - ratio × (v - t), with v the
          // midpoint measured from the centre of the resting image.
          this._scale = scale;
          this._tx = x - center.x - ratio * (pinch.x - center.x - pinch.tx);
          this._ty = y - center.y - ratio * (pinch.y - center.y - pinch.ty);
          this._applyZoom(img, false);
          return;
        }

        if (pan && e.touches.length === 1) {
          e.preventDefault();
          this._tx = pan.tx + (e.touches[0].clientX - startX);
          this._ty = pan.ty + (e.touches[0].clientY - startY);
          this._applyZoom(img, false);
          return;
        }

        // Anything that travels is a swipe, never half of a double tap.
        if (e.touches.length === 1) {
          const dx = Math.abs(e.touches[0].clientX - startX);
          const dy = Math.abs(e.touches[0].clientY - startY);
          if (dx > ProductLightbox.TAP_SLOP || dy > ProductLightbox.TAP_SLOP) moved = true;
        }
      },
      { passive: false }
    );

    this._stage.addEventListener(
      'touchend',
      (e) => {
        const img = this._activeImg();

        if (pinch && e.touches.length < 2) {
          pinch = null;
          if (img) this._applyZoom(img, true);
          // Settling back to 1 drops the cached box so the next gesture
          // re-measures, in case the image reflowed in between.
          if (!this._zoomed) this._resetZoom();
          // A finger still down carries straight on as a pan.
          if (e.touches.length === 1) {
            const started = startPan(e.touches[0]);
            pan = this._zoomed ? started : null;
            // The lift that ends this finger closes out a pinch, so it must not
            // also be read as the first half of a double tap.
            moved = true;
          }
          return;
        }

        pan = null;
        if (moved || e.touches.length) return;

        const touch = e.changedTouches[0];
        const now = Date.now();
        const near =
          Math.abs(touch.clientX - lastTapX) < ProductLightbox.DOUBLE_TAP_SLOP &&
          Math.abs(touch.clientY - lastTapY) < ProductLightbox.DOUBLE_TAP_SLOP;

        if (now - lastTap < ProductLightbox.DOUBLE_TAP_MS && near) {
          // Also suppresses the click the browser would synthesise from the tap.
          e.preventDefault();
          lastTap = 0;
          if (img) this._tapZoom(img, touch.clientX, touch.clientY);
          return;
        }

        // A stationary finger lifted while zoomed is still a candidate first tap
        // — that is how double-tap-to-exit is reached.
        lastTap = now;
        lastTapX = touch.clientX;
        lastTapY = touch.clientY;
      },
      { passive: false }
    );

    this._stage.addEventListener('touchcancel', () => {
      pinch = null;
      pan = null;
      const img = this._activeImg();
      if (img && this._zoomed) this._applyZoom(img, true);
    });
  }

  // ── Open trigger on the product gallery ───────────────────────────────

  _bindGalleryTrigger() {
    const root = this.closest('[data-product-page]');
    const slidesEl = root && root.querySelector('[data-gallery-slides]');
    if (!slidesEl) return;

    // A scroll-snap swipe on the mobile carousel can still emit a click, so
    // ignore clicks where the pointer travelled more than a few pixels.
    let moved = false;
    let startX = 0;
    let startY = 0;

    slidesEl.addEventListener(
      'touchstart',
      (e) => {
        moved = false;
        startX = e.changedTouches[0].clientX;
        startY = e.changedTouches[0].clientY;
      },
      { passive: true }
    );

    slidesEl.addEventListener(
      'touchmove',
      (e) => {
        const dx = Math.abs(e.changedTouches[0].clientX - startX);
        const dy = Math.abs(e.changedTouches[0].clientY - startY);
        if (dx > ProductLightbox.TAP_SLOP || dy > ProductLightbox.TAP_SLOP) moved = true;
      },
      { passive: true }
    );

    slidesEl.addEventListener('click', (e) => {
      if (moved) return;
      const slide = e.target.closest('[data-gallery-slide]');
      if (!slide) return;
      this.open(slide.dataset.mediaId);
    });
  }
}

if (!customElements.get('product-lightbox')) {
  customElements.define('product-lightbox', ProductLightbox);
}
